import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Modal, Button } from 'src/common';
import { useCreateMp, getUpdateCacheOnCreateMp } from 'src/models/matchingProfiles/hooks';
import styles from './index.module.scss';
import { useOpenSidebar } from 'src/shared/InfoSidebar/hooks';
import { SidebarMode } from 'src/shared/InfoSidebar/types';
import classNames from 'classnames';
import { JunctionLogic, ProfileType } from '@tendium/prom-types';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';
import { isNumber } from 'src/helpers';
interface FormState {
  name: string;
  saveSearch: string;
}

interface Props {
  onHide: () => void;
}

export const SaveAsNew: FC<Props> = props => {
  const { onHide } = props;

  const { t } = useTranslation();
  const openSidebar = useOpenSidebar();
  const [createMp, { loading: createMpLoading }] = useCreateMp();
  const [{ vars: searchArgs }, setActiveMpId] = useApiTenders();
  const [form] = Form.useForm();
  const [isDisabled] = useState<boolean | undefined>();

  const initialValues = useMemo((): FormState => {
    return {
      name: '',
      saveSearch: ''
    };
  }, []);

  const onSubmit = useCallback(
    async (values: FormState) => {
      const { name } = values;
      createMp({
        variables: {
          name,
          nutsCodesExact: searchArgs?.nutsCodesExact,
          nutsCodes: searchArgs?.nutsCodes,
          cpvCodesExact: searchArgs?.cpvCodesExact,
          cpvCodes: searchArgs?.cpvCodes,
          profileType: ProfileType.ExpiringContract,
          buyerOrgIds: searchArgs?.buyerOrgIds,
          buyerSearchStrings: searchArgs?.buyerSearchStrings,
          buyerTypes: searchArgs?.procuringAgencyTypes,
          keywords: searchArgs?.keywordArgs,
          supplierOrgIds: searchArgs?.supplierOrgIds,
          supplierSearchStrings: searchArgs?.supplierSearchStrings,
          supplierSearchLogic: searchArgs?.supplierSearchLogic ?? JunctionLogic.OR,
          contractValue: {
            currency: searchArgs.contractValueRange?.currency ?? undefined,
            max: isNumber(searchArgs.contractValueRange?.max) ? Number(searchArgs.contractValueRange?.max) : undefined,
            min: isNumber(searchArgs.contractValueRange?.min) ? Number(searchArgs.contractValueRange?.min) : undefined
          },
          contractValueIncludeMissingValue: searchArgs?.contractValueIncludeMissingValue
        },
        update: getUpdateCacheOnCreateMp(true)
      })
        .then(({ data }) => {
          const mpId = data?.createMatchingProfile.id;
          if (!!mpId) {
            setActiveMpId({ profileId: mpId });
            openSidebar({ id: mpId, mode: SidebarMode.EC_INFO });
          }
          onHide();
        })
        .catch(() => {
          form.setFields([
            {
              name: 'name',
              errors: [t('Common.unknownErrorDesc')]
            }
          ]);
        });
    },
    [
      createMp,
      searchArgs?.nutsCodesExact,
      searchArgs?.nutsCodes,
      searchArgs?.cpvCodesExact,
      searchArgs?.cpvCodes,
      searchArgs?.buyerOrgIds,
      searchArgs?.buyerSearchStrings,
      searchArgs?.procuringAgencyTypes,
      searchArgs?.keywordArgs,
      searchArgs?.supplierOrgIds,
      searchArgs?.supplierSearchStrings,
      searchArgs?.supplierSearchLogic,
      searchArgs?.contractValueRange,
      searchArgs?.contractValueIncludeMissingValue,
      onHide,
      setActiveMpId,
      openSidebar,
      form,
      t
    ]
  );

  return (
    <Modal title={t('ExpiringContracts.saveSearch')} visible onCancel={onHide} footer={null} maskClosable width={500}>
      <Form className={styles.form} onFinish={onSubmit} initialValues={initialValues} form={form} layout={'vertical'}>
        <Form.Item
          label={t('Common.name')}
          name={'name'}
          rules={
            !isDisabled
              ? [
                  {
                    required: true,
                    message: t('ExpiringContracts.nameRequired', { inputName: t('ExpiringContracts.nameRequired') })
                  },
                  { max: 50, message: t('ExpiringContracts.nameTooLong') }
                ]
              : []
          }
          className={classNames({
            [styles.isDisabled]: isDisabled
          })}
        >
          <Input data-type={'name'} type={'text'} placeholder={t('Common.name')} disabled={isDisabled} />
        </Form.Item>
        <div className={styles.formFooter}>
          <Button type={'text'} onClick={onHide}>
            {t('Common.cancel')}
          </Button>
          <Button type={'primary'} htmlType={'submit'} loading={createMpLoading}>
            {t('Common.add')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default SaveAsNew;
